import { useState, useEffect, useRef } from "react";
import { BASE_URL } from "../../config";
import { showSuccess, showError, showConfirm } from "../../utils/dialog";
import { FaTimes, FaFileAlt } from "react-icons/fa";
import { MdLibraryAdd } from "react-icons/md";
import DateInput from "../../components/DateInput";
import {
  STAFF_DOCUMENT_ACCEPT,
  validateStaffDocumentFile,
} from "../../utils/staffDocumentValidation";

const AddFileForm = ({ onClose, accountId }) => {
  
  const [filetype, setFiletype] = useState([]);
  const [selectedFiletype, setSelectedFileType] = useState(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [uploadedFile, setUploadedFile] = useState(null);
  const [expiryDate, setExpiryDate] = useState("");
  const containerRef = useRef(null);
  const fileInputRef = useRef(null);
  const isSinDocumentSelected =
    String(selectedFiletype?.type || "").trim() === "SIN Document";

  const handleSelectFileType = (type) => {
    setSelectedFileType(type);
    if (String(type?.type || "").trim() !== "SIN Document") {
      setExpiryDate("");
    }
    setShowDropdown(false);
  };

  useEffect(() => {
    let cancelled = false;
    const loadTypes = async () => {
      try {
        const res = await fetch(`${BASE_URL}/api/personnel/file-types`, { credentials: 'include' });
        const j = await res.json().catch(() => ({}));
        const rows = Array.isArray(j.data) ? j.data : (Array.isArray(j) ? j : []);
        if (!cancelled) setFiletype(rows.map(r => ({ id: r.type_id || r.id, type: r.name })));
      } catch (e) {
        console.error('Failed to load file types', e);
      }
    };
    loadTypes();
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // Handle file drop
  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const file = e.dataTransfer.files[0];
    if (file) {
      const validationMessage = validateStaffDocumentFile(file);
      if (validationMessage) {
        showError(validationMessage);
        return;
      }
      setUploadedFile(file);
    }
  };

  // Handle file browse
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const validationMessage = validateStaffDocumentFile(file);
      if (validationMessage) {
        showError(validationMessage);
        e.target.value = "";
        return;
      }
      setUploadedFile(file);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6 relative">
        {/* Close button */}
        <button 
          onClick={onClose} 
          className="absolute top-3 right-3 text-[#DC1F00] hover:bg-[#DC1F0033] p-3 rounded-full"
          >
          ✖
        </button>
  
        {/* Header */}
        <div className="flex">
          <MdLibraryAdd className="text-buttonRed text-lg w-5 h-5 mt-1 mr-2"/>
          <h2 className="text-xl text-formTitleText font-semibold mb-4">Add File</h2>
        </div>
        
        <div className="h-[1px] bg-[#DC1F004D]"></div>

         {/* Form */}
        <form className="flex-1 px-5 pt-6" onSubmit={async (e) => {
          e.preventDefault();
          try {
            if (!(await showConfirm('Send request to office/admin for review?'))) return;
            const account = accountId || localStorage.getItem('accountId');
            if (!account) return showError('Account not found');
            if (!selectedFiletype || !selectedFiletype.id) return showError('Select file type');
            if (!uploadedFile) return showError('Select a file to upload');

            const token = localStorage.getItem('token');
            const form = new FormData();
            form.append('file', uploadedFile);
            form.append('state', 'Pending review');
            form.append('note', 'User uploaded file');
            form.append('type_id', selectedFiletype.id);
            if (isSinDocumentSelected) {
              form.append("expiry_date", expiryDate || "");
            }

            const res = await fetch(`${BASE_URL}/api/personnel/accounts/${account}/files`, {
              method: 'POST',
              credentials: 'include',
              body: form,
              ...(token ? { headers: { Authorization: `Bearer ${token}` } } : {})
            });
            const j = await res.json().catch(() => ({}));
            if (!res.ok || !j?.success) return showError(j?.message || 'Failed to upload file');
            showSuccess('File created and uploaded (pending review)');
            onClose && onClose();
          } catch (err) {
            console.error('AddFileForm submit error', err);
            showError('Failed to upload file');
          }
        }}>
          {/* Dropdown */}
          <div className="relative mb-3" ref={containerRef}>
            <label className="block text-sm text-textGray mb-2">Type *</label>
            <div
              onClick={() => setShowDropdown(!showDropdown)}
              className="w-full border rounded-lg px-3 py-2 flex flex-wrap gap-2 cursor-pointer min-h-[40px]"
            >
              {selectedFiletype ? (
                <span className="px-2 py-1 rounded-full flex items-center gap-1">
                {selectedFiletype.type}
                </span>
              ) : (
                <span className="text-gray-500">Select File Type</span>
              )}
            </div>

            {showDropdown && (
              <div className="absolute left-0 top-full mt-1 w-full bg-white border rounded-lg shadow-lg max-h-64 overflow-y-auto z-50">
                {filetype.map((type) => (
                  <div
                    key={type.id}
                    className={`px-3 py-2 cursor-pointer hover:bg-red-100 ${
                      selectedFiletype?.id === type.id
                        ? "bg-red-50"
                        : ""
                    }`}
                    onClick={() => handleSelectFileType(type)}
                  >
                    {type.type}
                  </div>
                ))}
              </div>
            )}
          </div>

          {isSinDocumentSelected ? (
            <div className="mb-3">
              <label className="block text-sm text-textGray mb-2">
                Expiry date (optional)
              </label>
              <DateInput
                value={expiryDate}
                onChange={setExpiryDate}
                className="w-full border rounded-lg px-3 py-2"
              />
            </div>
          ) : null}

          {/* file upload field */}
          <div className="mb-12">
             <label className="block text-sm text-textGray mb-2">File *</label>
             <div 
                className="border-2 border-dashed rounded-lg p-6 flex flex-col items-center justify-center text-center transition-colors duration-200 hover:border-[#DC1F00] cursor-pointer"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
                onClick={() => fileInputRef.current.click()}
                required
              >
                {!uploadedFile ? (
                <>
                  <FaFileAlt className="text-gray-400 text-5xl mb-2" />
                  <p className="text-gray-500">Drag & Drop file here</p>
                  <p className="text-gray-400 text-sm mt-1">or click to upload</p>
                </>
              ) : (
                <div className="flex flex-col items-center">
                  <FaFileAlt className="text-[#DC1F00] text-5xl mb-2" />
                  <p className="text-gray-700 font-medium">{uploadedFile.name}</p>
                  <p className="text-gray-400 text-sm">
                    {(uploadedFile.size / 1024).toFixed(1)} KB
                  </p>
                </div>
              )}

              <input
                type="file"
                accept={STAFF_DOCUMENT_ACCEPT}
                ref={fileInputRef}
                className="hidden"
                onChange={handleFileChange}
              />
             </div>
          </div>

          {/* buttons section */}
          <div className="flex items-center justify-between gap-4 mb-2">
            <button
              onClick={onClose}
              className="w-full border-2 border-buttonWhite hover:bg-buttonWhite-hover text-[#DC1F00] font-semibold px-4 py-2 rounded-full"
            >
            Cancel
            </button>
            <button
              type="submit"
              className="w-full bg-buttonRed hover:bg-buttonRed-hover text-white font-semibold px-4 py-2 rounded-full"
            >
            Request Changes
            </button>
          </div>
          <p className="ml-4 py-1 text-xs">Requested change will be sent to our team to review and update.</p>
        </form>
      </div>
    </div>
  )

};

export default AddFileForm;
